if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    connectedCallback() {
      // Not lazy: returning from a product page can restore the scroll position far below the
      // toolbar, where the lazy init would never fire.
      if (this.hasAttribute('data-sticky-mobile')) this.initStickyMobile();
    }

    disconnectedCallback() {
      if (!this.stickyScrollHandler) return;
      this.setPinned(false);
      window.removeEventListener('scroll', this.stickyScrollHandler);
      window.removeEventListener('resize', this.stickyResizeHandler);
      window.removeEventListener('on:breakpoint-change', this.stickyResizeHandler);
      this.stickyScrollHandler = null;
    }

    /**
     * Pins the filter + sort row under the header on small screens once it scrolls up to it, so
     * filters stay reachable while browsing (large screens have the sticky sidebar instead).
     * The same elements are pinned, and a placeholder keeps their space so the grid doesn't jump.
     */
    initStickyMobile() {
      this.primary = this.querySelector('.products-toolbar__primary');
      this.placeholder = this.querySelector('.products-toolbar__primary-placeholder');
      if (!this.primary || !this.placeholder || this.stickyScrollHandler) return;

      this.header = document.querySelector('store-header');
      this.pinned = false;

      let ticking = false;
      this.stickyScrollHandler = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.updateSticky();
        });
      };
      // Layout changed: measure again from the resting position.
      this.stickyResizeHandler = () => {
        this.setPinned(false);
        this.updateSticky();
      };

      window.addEventListener('scroll', this.stickyScrollHandler, { passive: true });
      window.addEventListener('resize', this.stickyResizeHandler);
      window.addEventListener('on:breakpoint-change', this.stickyResizeHandler);
      this.updateSticky();
    }

    /**
     * Pins or releases the filter + sort row based on its resting position and the header bottom.
     */
    updateSticky() {
      if (theme.mediaMatches.lg) {
        this.setPinned(false);
        return;
      }

      // Header bottom is 0 once a non-sticky header has scrolled away
      const headerBottom = this.header ? Math.max(this.header.getBoundingClientRect().bottom, 0) : 0;
      const restingTop = this.pinned
        ? this.placeholder.getBoundingClientRect().top
        : this.getPrimaryRect().top;

      this.setPinned(restingTop < headerBottom);
      if (!this.pinned) return;

      this.primary.style.top = `${headerBottom}px`;

      // Slide away once the product listing has scrolled past, so the footer isn't covered
      const listingBottom = this.parentElement.getBoundingClientRect().bottom;
      this.primary.classList.toggle(
        'is-past-end',
        listingBottom < headerBottom + this.primary.offsetHeight
      );
    }

    /**
     * Toggles the pinned state, sizing the placeholder to the row it stands in for.
     * @param {boolean} pinned - Whether the row should be pinned.
     */
    setPinned(pinned) {
      if (pinned === this.pinned) return;

      if (pinned) {
        const rect = this.getPrimaryRect();
        this.placeholder.style.width = `${rect.width}px`;
        this.placeholder.style.height = `${rect.height}px`;
      } else {
        this.primary.style.top = '';
        this.primary.classList.remove('is-past-end');
      }

      this.placeholder.hidden = !pinned;
      this.primary.classList.toggle('is-pinned', pinned);
      this.pinned = pinned;
    }

    /**
     * Gets the bounding box of the filter + sort row. The wrapper uses display: contents while
     * resting, so it has no box of its own - combine its visible children instead.
     * @returns {{top: number, width: number, height: number}}
     */
    getPrimaryRect() {
      const rects = [...this.primary.children]
        .map((child) => child.getBoundingClientRect())
        .filter((rect) => rect.width && rect.height);
      if (!rects.length) return { top: Infinity, width: 0, height: 0 };

      const top = Math.min(...rects.map((rect) => rect.top));
      const left = Math.min(...rects.map((rect) => rect.left));
      return {
        top,
        width: Math.max(...rects.map((rect) => rect.right)) - left,
        height: Math.max(...rects.map((rect) => rect.bottom)) - top
      };
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        // Set the layout to the layout in localstorage
        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
        }
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results > .grid');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
      } else {
        this.filtersComponent.open();
      }
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     */
    handleLayoutChange(evt) {
      this.productsGrid.dataset.layout = evt.target.value;
      theme.storageUtil.set('products-layout', evt.target.value);
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
